/**
 * Fremden Text lesbar machen.
 *
 * ## Warum das nötig ist – gemessen am 02.08.2026
 *
 * Die Beschreibungen in Work sind zur Hälfte **HTML**, und niemand hat das so gewollt:
 *
 * ```
 * GitLab         724 von 1.503 Beschreibungen enthalten HTML
 * NetCon       1.252 von 1.280
 * ```
 *
 * Bei NetCon ist es der Ursprung – Tickets entstehen aus E-Mails. Bei GitLab ist es ein
 * **Abzug davon**: In den Issue-Beschreibungen steht der Ticketinhalt mitsamt dem
 * Outlook-HTML, das die E-Mail mitbrachte. Eine Beschreibung sah so aus:
 *
 * ```
 * Ticket: https://web.netcon.cloud/Tickets/96075<br>Absender: <br>…
 * <meta http-equiv="Content-Type" content="text/html; charset=iso-8859-1">
 * <style> p.MsoNormal, li.MsoNormal { margin:0cm; font-size:12.0pt; … } </style>
 * <!--[if gte mso 9]><xml><o:shapedefaults v:ext="edit" spidmax="1026" /></xml><![endif]-->
 * ```
 *
 * 15.059 Zeichen, davon vielleicht 300 mit Inhalt. Work zeigte das **roh** an.
 *
 * ## Warum zu Text und nicht gerendert
 *
 * Man könnte das HTML darstellen statt es zu entfernen. Für einen von Hand geschriebenen
 * Absatz wäre das besser – für einen Word-Export ist es schlechter: `mso-`-Stile,
 * Schriftdefinitionen und bedingte Kommentare ergeben gerendert eine Seite, die mit dem
 * Rest der Oberfläche nichts zu tun hat, und ein Sanitizer müsste alles davon einzeln
 * kennen. **Der Inhalt ist der Text**; alles andere ist Verpackung einer E-Mail, die
 * jemand vor Monaten geschrieben hat.
 *
 * ## Was erhalten bleibt
 *
 * Absätze. `inhaltAlsText` im NetCon-Konnektor macht aus allem **eine Zeile** – richtig
 * für einen Feed-Eintrag mit zwei Sätzen, falsch für eine Beschreibung mit fünfzehn
 * Absätzen. Hier werden `<br>`, `</p>`, `</div>` und Tabellenzeilen zu Zeilenumbrüchen.
 */

/** Elemente, deren **Inhalt** mit weg muss – nicht nur die Klammern. */
const GANZ_WEG = /<(script|style|head|title|xml|o:[a-z]+|v:[a-z]+|w:[a-z]+)\b[^>]*>[\s\S]*?<\/\1>/gi

/** Bedingte Kommentare von Word (`<!--[if gte mso 9]> … <![endif]-->`) und normale. */
const KOMMENTARE = /<!--[\s\S]*?-->/g

/** Alles, was zu einem Absatzwechsel führt. */
const UMBRUCH = /<\s*(br|\/p|\/div|\/tr|\/li|\/h[1-6]|\/blockquote)\b[^>]*>/gi

/**
 * **Eingebettete Dateien** – `data:image/png;base64,iVBORw0KGgo…`
 *
 * Outlook hängt Bilder nicht an, es schreibt sie **in** die Mail. Ein Firmenlogo unter der
 * Signatur sind schnell 5.000 Zeichen, und für einen Menschen ist davon kein einziges
 * lesbar.
 *
 * Das Muster hört bewusst **nicht** an einem Anführungszeichen auf, sondern an allem, was
 * nicht mehr zum Base64-Alphabet gehört: die Daten stehen auch dann noch da, wenn der Tag
 * um sie herum längst zerschnitten ist – und genau so kam es (siehe `ABGESCHNITTEN`).
 */
const DATEN_URI = /data:[a-z0-9.+/-]+;base64,\s*[A-Za-z0-9+/=\s]*/gi

/**
 * **Ein Tag, der nie zugeht.**
 *
 * `<img src="…` ohne schließende Klammer entsteht, wenn ein langer Text irgendwo gekürzt
 * wurde – bei NetCon bei 8.000 Zeichen. `<[^>]+>` greift dann nicht mehr, und der halbe
 * Tag bleibt als Text stehen. Gefunden an Ticket **#48513** (03.08.2026): dort überlebten
 * **5.277 Zeichen Base64** als ein einziges Wort, und Nils musste im Aufgabendetail
 * seitwärts scrollen, bis es aufhörte.
 *
 * Auch `<!` (14.09.2026): ein bei 400 Zeichen gekürztes Outlook-HTML endete in
 * `<!--[if !mso]>`, und die neun Zeichen `<!--[if !` standen als Vorschau im Eingang.
 */
const ABGESCHNITTEN = /<[a-z!][^>]*$/i

const ENTITAETEN: Record<string, string> = {
  '&nbsp;': ' ',
  '&gt;': '>',
  '&lt;': '<',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&ndash;': '–',
  '&mdash;': '—',
  '&hellip;': '…',
  '&auml;': 'ä',
  '&ouml;': 'ö',
  '&uuml;': 'ü',
  '&Auml;': 'Ä',
  '&Ouml;': 'Ö',
  '&Uuml;': 'Ü',
  '&szlig;': 'ß',
  '&euro;': '€',
  // `&amp;` zuletzt, sonst würde `&amp;gt;` zu `>` statt zu `&gt;`.
  '&amp;': '&',
}

/**
 * Eingebettete Dateien aus einem Text entfernen – **vor** allem anderen.
 *
 * Steht hier und nicht nur in `htmlZuText`, weil es an einer zweiten Stelle gebraucht wird:
 * der NetCon-Import kürzt Beschreibungen bei 8.000 Zeichen, und wenn davon 5.277 auf ein
 * Signaturlogo entfallen, wird der eigentliche Inhalt weggeschnitten, um ein Bild zu
 * behalten, das nie jemand sieht. **Erst putzen, dann kürzen.**
 */
export function ohneEingebetteteDateien(text: string): string {
  return text.replace(DATEN_URI, ' ')
}

/**
 * Enthält der Text Markup, das ihn unlesbar macht?
 *
 * Bewusst eng: ein einzelnes `<` oder ein `a < b` in einem Codeblock ist **kein** HTML.
 * Gesucht wird nach einem Element mit schließender Klammer – das schreibt niemand
 * versehentlich.
 */
export function enthaeltHtml(text: string): boolean {
  return /<(br|p|div|span|table|tr|td|meta|html|head|style|font|o:p)\b[^>]*>/i.test(text)
}

/**
 * HTML zu lesbarem Text, **mit Absätzen**.
 *
 * Kein Ersatz für einen Sanitizer: das Ergebnis wird als Text dargestellt, nicht als
 * Markup. Genau deshalb ist es sicher – es gibt nichts zu entschärfen, wenn nichts
 * ausgeführt wird.
 */
export function htmlZuText(inhalt: string): string {
  let text = inhalt
    .replace(KOMMENTARE, ' ')
    .replace(GANZ_WEG, ' ')
    /**
     * **Bilddaten zuerst, Tags danach.** Umgekehrt wäre die Reihenfolge wirkungslos: ein
     * unbeendeter `<img`-Tag wird von keiner Tag-Regel erfasst, und dann steht der ganze
     * Base64-Block noch da.
     */
    .replace(DATEN_URI, ' ')
    /**
     * **Auch unpaarige Reste.** Word schreibt `<o:p></o:p>` und `<![endif]>`; ein
     * `<style>` ohne Gegenstück kommt vor, wenn jemand von Hand gekürzt hat. Ohne diese
     * Zeile bliebe der halbe Stilblock stehen.
     */
    .replace(/<\/?(?:o|v|w|x):[a-z]+[^>]*>/gi, ' ')
    .replace(/<!\[[^\]]*\]>/g, ' ')
    .replace(UMBRUCH, '\n')
    .replace(/<[^>]+>/g, ' ')
    // Der Rest eines Tags, dem die schließende Klammer fehlt, weil davor gekürzt wurde.
    .replace(ABGESCHNITTEN, ' ')

  for (const [entitaet, zeichen] of Object.entries(ENTITAETEN)) {
    text = text.split(entitaet).join(zeichen)
  }
  // Numerische Entitäten (`&#8217;`) – nach den benannten, damit `&amp;#39;` stimmt.
  text = text.replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))

  return (
    text
      // Leerraum innerhalb einer Zeile zusammenfassen, Umbrüche behalten.
      .replace(/[^\S\n]+/g, ' ')
      .replace(/ *\n */g, '\n')
      // Mehr als eine Leerzeile sagt nichts – Word erzeugt Dutzende.
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  )
}

/**
 * Text so aufbereiten, wie er angezeigt werden soll.
 *
 * Steckt HTML drin, wird es zu Text; sonst bleibt alles, wie es ist. **Markdown wird
 * nicht angetastet** – `**fett**` und `- Liste` sind auch ungerendert lesbar, und ein
 * halbherziger Markdown-Ersatz („Sternchen weg") würde Codebeispiele zerstören.
 */
export function lesbarerText(inhalt: string | null | undefined): string {
  if (!inhalt) return ''
  return enthaeltHtml(inhalt) ? htmlZuText(inhalt) : inhalt
}
